import { uploadEpisodeThumbnail, uploadEpisodeVideo, type ChunkedUploadController, type ChunkedUploadProgress } from '@/services/storage'
import { apiPost } from '../../lib/api-client'

export type WebEpisodeJobStatus = 'queued' | 'thumbnail' | 'video' | 'saving' | 'done' | 'error'

export interface WebEpisodeJob {
  id: string
  file: File
  seriesId: string
  episodeNumber: number
  title: string
  description?: string
}

export interface WebEpisodeUploadProgress {
  episodeId: string
  episodeNumber: number
  status: WebEpisodeJobStatus
  videoPct: number
  error?: string
}

export interface ParallelUploadOptions {
  jobs: WebEpisodeJob[]
  concurrency?: number
  onEpisodeProgress?: (progress: WebEpisodeUploadProgress[]) => void
  onStageChange?: (stage: string) => void
}

export interface ParallelUploadController {
  controllers: ChunkedUploadController[]
  promise: Promise<void>
  abort: () => void
}

export function runParallelEpisodeUpload(options: ParallelUploadOptions): ParallelUploadController {
  const { jobs, concurrency = 3, onEpisodeProgress, onStageChange } = options

  const controllers: ChunkedUploadController[] = []
  let aborted = false

  const promise = new Promise<void>((resolve, reject) => {
    if (jobs.length === 0) {
      resolve()
      return
    }

    const statuses: WebEpisodeUploadProgress[] = jobs.map((job) => ({
      episodeId: job.id,
      episodeNumber: job.episodeNumber,
      status: 'queued',
      videoPct: 0,
    }))

    const update = (episodeId: string, patch: Partial<WebEpisodeUploadProgress>) => {
      const idx = statuses.findIndex((s) => s.episodeId === episodeId)
      if (idx === -1) return
      statuses[idx] = { ...statuses[idx], ...patch }
      onEpisodeProgress?.([...statuses])
    }

    let idx = 0
    let running = 0
    let settled = 0
    const total = jobs.length
    const errors: string[] = []

    const next = () => {
      if (aborted) return
      while (running < concurrency && idx < total) {
        const job = jobs[idx++]
        running++

        const doOne = async () => {
          const epLocalId = job.id
          const epId = `${job.episodeNumber}-${Date.now()}`

          try {
            onStageChange?.(`Episode ${job.episodeNumber}: thumbnail upload…`)
            update(epLocalId, { status: 'thumbnail' })

            const thumb = await uploadEpisodeThumbnail(job.file as unknown as string, job.seriesId, epId)
            if (!thumb) {
              throw new Error(`Failed to upload thumbnail for Episode ${job.episodeNumber}`)
            }

            onStageChange?.(`Episode ${job.episodeNumber}: video upload…`)
            update(epLocalId, { status: 'video', videoPct: 0 })

            const ctrl = uploadEpisodeVideo(
              job.file as unknown as string,
              job.seriesId,
              epId,
              (p: ChunkedUploadProgress) => {
                const pct = p.total > 0 ? Math.round((p.loaded / p.total) * 100) : 0
                update(epLocalId, { videoPct: pct })
              },
            )
            controllers.push(ctrl)
            const video = await ctrl.promise

            onStageChange?.(`Episode ${job.episodeNumber}: saving metadata…`)
            update(epLocalId, { status: 'saving', videoPct: 100 })

            await apiPost(`/creator/series/${encodeURIComponent(job.seriesId)}/episodes`, {
              episodeNumber: job.episodeNumber,
              title: job.title,
              description: job.description ?? '',
              videoKey: video.key,
              thumbnailKey: thumb.key,
            })

            update(epLocalId, { status: 'done' })
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err)
            errors.push(message)
            update(epLocalId, { status: 'error', error: message })
          }
        }

        doOne()
          .catch((err) => {
            const message = err instanceof Error ? err.message : String(err)
            errors.push(message)
          })
          .finally(() => {
            running--
            settled++
            if (settled === total) {
              if (errors.length > 0) {
                reject(new Error(errors.join('; ')))
              } else {
                resolve()
              }
            } else {
              next()
            }
          })
      }
    }

    onStageChange?.(`Uploading ${jobs.length} episode(s)…`)
    next()
  })

  const abort = () => {
    aborted = true
    controllers.forEach((c) => c.abort())
  }

  return { controllers, promise, abort }
}

